import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Upload, Cpu, FileCheck, ArrowRight, Sparkles, Shield, Clock, Zap, RefreshCw, MessageSquare } from 'lucide-react';
import MatrixBackground from '@/MatrixBackground';
import MarketingHeader from '@/components/MarketingHeader';
import usePageSEO from '@/hooks/usePageSEO';

const steps = [
  {
    num: '01',
    icon: Upload,
    title: 'Provide Your Input',
    description: 'Upload a document, paste text, type instructions, or even record a voice note — whatever your workflow demands. Lawra accepts PDFs, common document formats, and direct text input.',
    detail: 'No special formatting required. Simply drag and drop, paste, type, or speak — and Lawra takes care of the rest.',
  },
  {
    num: '02',
    icon: Cpu,
    title: 'AI Analyses in Seconds',
    description: 'Lawra\'s legal AI — purpose-built for solicitors and lawyers — processes your input instantly, understanding legal language, clause structures, and risk patterns across UK and international law.',
    detail: 'Each tool applies a specialised analysis framework — contract review looks for risk clauses, due diligence extracts key findings, summarisation distils core arguments.',
  },
  {
    num: '03',
    icon: FileCheck,
    title: 'Review Your Results',
    description: 'Receive structured, actionable output tailored to the tool you used — whether that\'s a risk report, a drafted document, a case law summary, or deposition questions.',
    detail: 'Copy, export, or refine the output. Use Ask Lawra to dig deeper into any specific point or follow up on the results.',
  },
  {
    num: '04',
    icon: RefreshCw,
    title: 'Iterate and Refine',
    description: 'Use your results as a starting point. Run follow-up analyses, compare document versions, or ask Lawra for clarification on specific clauses or legal points.',
    detail: 'Credits are only used when you run a tool — browsing, reading results, and navigating the platform is always free.',
  },
];

const tools = [
  { name: 'Contract Review', desc: 'Identify risk clauses, unusual terms, and liabilities', icon: FileCheck },
  { name: 'Due Diligence', desc: 'Extract key findings and flag compliance gaps', icon: Shield },
  { name: 'Legal Research', desc: 'Find relevant case law, precedents, and authorities', icon: Sparkles },
  { name: 'Document Generation', desc: 'Draft legal documents from instructions or templates', icon: FileCheck },
  { name: 'Deposition Prep', desc: 'Generate questions and analyse witness statements', icon: MessageSquare },
  { name: 'Document Comparison', desc: 'Compare versions and track clause-level changes', icon: RefreshCw },
  { name: 'Legal Summarisation', desc: 'Summarise lengthy documents into concise overviews', icon: FileCheck },
  { name: 'Email Response', desc: 'Draft professional legal correspondence', icon: MessageSquare },
  { name: 'Ask Lawra', desc: 'AI chatbot for quick legal queries and research', icon: Sparkles },
];

const HowItWorks = () => {
  const navigate = useNavigate();

  usePageSEO({
    title: 'How It Works - AI Legal Tools in 4 Simple Steps',
    description: 'See how Lawra AI works: upload a legal document, get AI-powered analysis in seconds, review structured results, and iterate. Contract review, due diligence, legal research and more.',
    path: '/how-it-works',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Use Lawra AI for Legal Work",
      "description": "Provide your input, get AI-powered legal analysis in seconds, review structured results, and iterate.",
      "step": steps.map((s, i) => ({
        "@type": "HowToStep",
        "position": i + 1,
        "name": s.title,
        "text": s.description,
      })),
    },
  });

  return (
    <div className="landing-page">
      <MatrixBackground />
      <MarketingHeader />

      {/* Hero */}
      <section className="pricing-hero" data-testid="hiw-hero">
        <div className="pricing-hero-inner">
          <p className="uc-hero-label">How It Works</p>
          <h1 className="uc-hero-title">From Document to Insight in Seconds</h1>
          <p className="uc-hero-subtitle">
            Lawra AI turns hours of manual legal work into seconds of AI-powered analysis. Built specifically for lawyers and solicitors — here's how.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="hiw-steps" data-testid="hiw-steps">
        <div className="hiw-steps-inner">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <React.Fragment key={i}>
                <div className="hiw-step" data-testid={`hiw-step-${i + 1}`}>
                  <div className="hiw-step-left">
                    <div className="hiw-step-num">{step.num}</div>
                    <div className="hiw-step-icon"><Icon size={28} /></div>
                  </div>
                  <div className="hiw-step-content">
                    <h2 className="hiw-step-title">{step.title}</h2>
                    <p className="hiw-step-desc">{step.description}</p>
                    <p className="hiw-step-detail">{step.detail}</p>
                  </div>
                </div>
                {i < steps.length - 1 && (
                  <div className="hiw-step-arrow" data-testid={`hiw-arrow-${i + 1}`}>
                    <ArrowRight size={28} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
          <div className="hiw-steps-cta">
            <Button
              className="try-now-btn"
              size="lg"
              data-testid="hiw-steps-cta"
              onClick={async () => {
                try {
                  const { auth } = await import('../firebase');
                  if (auth.currentUser) navigate('/dashboard');
                  else navigate('/onboarding');
                } catch { navigate('/onboarding'); }
              }}
            >
              Try Now for Free
            </Button>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="hiw-benefits" data-testid="hiw-benefits">
        <div className="hiw-benefits-inner">
          <h2 className="pricing-savings-title" style={{ textAlign: 'center', marginBottom: '2.5rem' }}>Why Lawyers Choose Lawra AI</h2>
          <div className="hiw-benefits-grid">
            <div className="hiw-benefit">
              <Clock size={24} className="hiw-benefit-icon" />
              <h4>Save Hours Every Day</h4>
              <p>Tasks that take 3-5 hours manually are completed in under a minute. Spend your time on strategy and client relationships, not paperwork.</p>
            </div>
            <div className="hiw-benefit">
              <Shield size={24} className="hiw-benefit-icon" />
              <h4>Catch What You'd Miss</h4>
              <p>AI analyses every clause, every line. No fatigue, no oversights. Risk clauses and compliance gaps are flagged consistently every time.</p>
            </div>
            <div className="hiw-benefit">
              <Zap size={24} className="hiw-benefit-icon" />
              <h4>No Learning Curve</h4>
              <p>Upload a document and get results. No training sessions, no complex configuration. If you can use email, you can use Lawra AI.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Available Tools */}
      <section className="hiw-tools" data-testid="hiw-tools">
        <div className="hiw-tools-inner">
          <h2 className="pricing-savings-title" style={{ textAlign: 'center', marginBottom: '0.75rem' }}>Available Tools</h2>
          <p className="pricing-savings-subtitle" style={{ textAlign: 'center', marginBottom: '2.5rem' }}>Each tool is purpose-built for a specific legal workflow</p>
          <div className="hiw-tools-grid">
            {tools.map((t, i) => {
              const TIcon = t.icon;
              return (
                <div key={i} className="hiw-tool-card" data-testid={`hiw-tool-${i}`}>
                  <TIcon size={20} className="hiw-tool-icon" />
                  <div>
                    <h4 className="hiw-tool-name">{t.name}</h4>
                    <p className="hiw-tool-desc">{t.desc}</p>
                  </div>
                </div>
              );
            })}
            <div className="hiw-tool-card hiw-tool-more" data-testid="hiw-tool-more" onClick={() => navigate('/pricing')}>
              <ArrowRight size={20} className="hiw-tool-icon" />
              <div>
                <h4 className="hiw-tool-name">And Many More</h4>
                <p className="hiw-tool-desc">New tools added regularly based on what lawyers need most</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section" data-testid="hiw-cta">
        <div className="cta-content">
          <h2>Ready to Try It?</h2>
          <p>Start with 20 free credits. No credit card required.</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              className="try-now-btn"
              size="lg"
              data-testid="hiw-cta-trial"
              onClick={async () => {
                try {
                  const { auth } = await import('../firebase');
                  if (auth.currentUser) navigate('/dashboard');
                  else navigate('/onboarding');
                } catch { navigate('/onboarding'); }
              }}
            >
              Start Free Trial
            </Button>
            <Button variant="ghost" className="use-cases-nav-link" size="lg" onClick={() => navigate('/pricing')} data-testid="hiw-cta-pricing">
              View Pricing <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
